import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import Duration from '@lean-body/components/atoms/duration';
import PlaylistPlayButton from '@lean-body/components/atoms/playlist_play_button';
import { MediaBreakPointUp, Colors } from '@lean-body/components/styles/variables';
import ViewCount from '@lean-body/components/atoms/view_count';
import { ButtonFavorite } from '@lean-body/components/atoms/button_favorite';
import { FireIcon } from '@lean-body/components/atoms/icons/fire_icon';
import { useXlMediaQuery } from '@lean-body/src/util/media_query_hooks';
export const PlaylistLessonCard = ({ lesson, isNextLesson = false, isChallenge, viewCount, isFavorite, onClickPlayButton, onClickLessonFavoriteButton, classObject, }) => {
    const isXL = useXlMediaQuery();
    const [isFavorited, setIsFavorited] = React.useState(isFavorite);
    const [favoriteRequesting, setFavoriteRequesting] = React.useState(false);
    const handleOnClickCard = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (lesson.isDayOff) {
            return;
        }
        onClickPlayButton && onClickPlayButton(lesson);
    };
    const handleOnClickLessonFavoriteButton = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (lesson.isDayOff || favoriteRequesting) {
            return;
        }
        setFavoriteRequesting(true);
        onClickLessonFavoriteButton(lesson, isFavorited).then(() => {
            setFavoriteRequesting(false);
            setIsFavorited(!isFavorited);
        });
    };
    const styles = StyleSheet.create({
        wrapper: {
            position: 'relative',
            display: 'flex',
            width: '100%',
            height: 104,
            maxWidth: 944,
            cursor: onClickPlayButton ? 'pointer' : 'unset',
            backgroundColor: Colors.White,
            borderRadius: '15px',
            padding: 10,
            [MediaBreakPointUp.MD]: {
                padding: 16,
                height: 160,
            },
        },
        header: {
            position: 'relative',
            flexBasis: '47%',
            backgroundImage: `url(${lesson.thumbnailURL})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            borderRadius: '10px',
            overflow: 'hidden',
            [MediaBreakPointUp.MD]: {
                flexBasis: 228,
            },
        },
        playButton: {
            position: 'absolute',
            top: 'calc(50% - 15px)',
            left: 'calc(50% - 15px)',
        },
        duration: {
            position: 'absolute',
            right: 0,
            bottom: 0,
        },
        body: {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            flexBasis: '50%',
            padding: '8px 0 0 16px',
            transition: 'all 0.2s ease-out',
            ':link': {
                color: Colors.Black,
            },
            ':visited': {
                color: Colors.Black,
            },
            ':active': {
                color: Colors.Black,
            },
            [MediaBreakPointUp.MD]: {
                justifyContent: 'flex-start',
                padding: '0 0 0 16px',
                flexBasis: 680,
                ':hover': {
                    color: onClickPlayButton ? Colors.PrimaryDark : 'inherit',
                },
            },
        },
        lessonName: {
            display: '-webkit-box',
            '-webkit-box-orient': 'vertical',
            '-webkit-line-clamp': '3',
            overflow: 'hidden',
            margin: '4px 0 0',
            fontSize: 13,
            fontWeight: 'bold',
            lineHeight: 1.5,
            [MediaBreakPointUp.MD]: {
                fontSize: 16,
            },
        },
        trainerBox: {
            display: 'flex',
            alignItems: 'center',
            margin: '8px 0 11px',
            fontSize: 11,
            lineHeight: '11px',
            color: Colors.GrayDarkest,
            [MediaBreakPointUp.MD]: {
                margin: '4px 0 11px',
            },
        },
        calIcon: {
            marginRight: 3,
        },
        kcal: {
            marginRight: 20,
        },
        trainer: {
            marginRight: 10,
            display: 'flex',
            alignItems: 'center',
        },
        trainerImg: {
            height: 23,
            width: 23,
            border: `1px solid ${Colors.Gray}`,
            marginRight: 7,
            borderRadius: 100,
        },
        trainerName: {
            fontSize: 10,
            fontWeight: 'bold',
        },
        isNextLesson: {
            backgroundColor: Colors.BlueLighterB,
            border: `2px solid ${Colors.Primary}`,
        },
        lessonDescription: {
            fontSize: 12,
            lineHeight: '1.75',
            margin: 0,
            display: '-webkit-box',
            '-webkit-box-orient': 'vertical',
            '-webkit-line-clamp': '3',
            overflow: 'hidden',
        },
        viewCount: {
            position: 'absolute',
            top: 6,
            right: 6,
            [MediaBreakPointUp.MD]: {
                top: 8,
                right: 8,
            },
        },
        favoriteBtn: {
            position: 'relative',
            width: 24,
            height: 24,
            [MediaBreakPointUp.MD]: {
                width: 30,
                height: 30,
            },
        },
        favoriteIcon: {
            fontSize: 12,
            [MediaBreakPointUp.MD]: {
                fontSize: 14,
            },
        },
    });
    const showNextLesson = () => isNextLesson && !lesson.isCompleted;
    const wrapperClass = [css(styles.wrapper, classObject), showNextLesson() && css(styles.isNextLesson)].join(' ');
    const showTrainer = () => {
        if (!isXL) {
            return false;
        }
        if (!isChallenge) {
            return false;
        }
        if (lesson.isDayOff) {
            return false;
        }
        return true;
    };
    return (React.createElement("div", { className: wrapperClass, onClick: handleOnClickCard, "data-test": "playlistLessonCard" },
        React.createElement("div", { className: css(styles.header) },
            onClickLessonFavoriteButton && (React.createElement(ButtonFavorite, { onClick: handleOnClickLessonFavoriteButton, isStopped: !isFavorited, buttonClassName: css(styles.favoriteBtn), iconClassName: css(styles.favoriteIcon) })),
            !lesson.isDayOff && onClickPlayButton && (React.createElement("div", { className: css(styles.playButton) },
                React.createElement(PlaylistPlayButton, { size: "small" }))),
            React.createElement(ViewCount, { className: css(styles.viewCount), viewCount: viewCount }),
            !lesson.isDayOff && React.createElement(Duration, { className: css(styles.duration), duration: lesson.duration })),
        React.createElement("div", { className: css(styles.body) },
            React.createElement("div", { className: css(styles.lessonName) }, lesson.name),
            React.createElement("div", { className: css(styles.trainerBox) },
                React.createElement(FireIcon, { classObject: styles.calIcon }),
                React.createElement("div", { className: css(styles.kcal) },
                    lesson.minCal,
                    " - ",
                    lesson.maxCal,
                    " kcal"),
                showTrainer() &&
                    lesson.trainers.map((v, i) => {
                        return (React.createElement("div", { className: css(styles.trainer), key: i },
                            React.createElement("img", { className: css(styles.trainerImg), src: v.thumbnailURL }),
                            React.createElement("div", { className: css(styles.trainerName) }, v.name)));
                    })),
            isXL && React.createElement("div", { className: css(styles.lessonDescription) }, lesson.description))));
};
